<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { ComponentPublicInstance } from 'vue'
import { TheaterFeed, CreationFeed } from '@/components/feed-player/index'
import DataFeedback from '@/components/data-feedback.vue'
import { useHomeFeed } from '@/hooks/useHomeFeed'

const { t } = useI18n()
const router = useRouter()

const {
  activeTab,
  recommend,
  creation,
  gesture,
  viewportRef,
  recommendPlayerRef,
  creationPlayerRef,
  creationCategoryScrollRef,
  creationCategoryButtonRefs,
  handleCreationCategoryChange,
  stopCategoryTouch,
} = useHomeFeed()

const setCreationCategoryButtonRef = (categoryId: number) => (el: Element | ComponentPublicInstance | null) => {
  if (el instanceof HTMLButtonElement) {
    creationCategoryButtonRefs.value[categoryId] = el
    return
  }

  if (el && '$el' in el && el.$el instanceof HTMLButtonElement) {
    creationCategoryButtonRefs.value[categoryId] = el.$el
    return
  }

  creationCategoryButtonRefs.value[categoryId] = null
}

const onRecommendIndexChange = (index: number) => {
  recommend.handleIndexChange(index, recommendPlayerRef.value ?? undefined)
}

const onCreationIndexChange = (index: number) => {
  creation.handleIndexChange(index, creationPlayerRef.value ?? undefined)
}

const onSelectEpisode = (episodeId: number) => {
  const v = recommend.items.value[recommendPlayerRef.value?.currentIndex ?? -1]
  if (!v) return
  router.push(`/theater/detail/${v.ScriptIdKey}?episodeId=${episodeId}`)
}

const onRemixCreation = (creationId: number) => {
  router.push(`/creation?sourceCreationId=${creationId}`)
}
</script>

<template>
  <div class="page-content relative h-full w-full overflow-hidden bg-black">
    <div ref="viewportRef" class="relative h-full w-full overflow-hidden">
      <div class="pointer-events-none absolute inset-x-0 top-0 z-40 flex justify-center pt-4">
        <div class="pointer-events-auto flex rounded-full border border-white/10 bg-black/35 p-1 backdrop-blur-md">
          <button
            type="button"
            class="rounded-full px-6 py-1 text-sm font-medium transition-colors"
            :class="activeTab === 'recommend' ? 'bg-white text-black!' : 'text-white/75'"
            @click="gesture.switchTab('recommend')"
          >
            {{ t('home.page.tabs.recommend') }}
          </button>
          <button
            type="button"
            class="rounded-full px-6 py-1 text-sm font-medium transition-colors"
            :class="activeTab === 'creation' ? 'bg-white text-black!' : 'text-white/75'"
            @click="gesture.switchTab('creation')"
          >
            {{ t('home.page.tabs.creation') }}
          </button>
        </div>
      </div>

      <div
        class="h-full overflow-hidden"
        @touchstart.capture="gesture.onTouchStartCapture"
        @touchmove.capture="gesture.onTouchMoveCapture"
        @touchend.capture="gesture.onTouchEndCapture"
        @touchcancel.capture="gesture.onTouchCancelCapture"
      >
        <div class="flex h-full" :style="gesture.carouselTrackStyle.value">
          <!-- 推荐 -->
          <section class="relative h-full shrink-0" :style="{ width: gesture.sceneWidth.value }">
            <TheaterFeed
              v-if="recommend.formattedItems.value.length > 0"
              ref="recommendPlayerRef"
              :items="recommend.formattedItems.value"
              preset="recommend"
              :playback-enabled="gesture.recommendPlaybackEnabled.value"
              :navigation-enabled="gesture.recommendNavigationEnabled.value"
              :show-comments="true"
              :show-episodes="true"
              @index-change="onRecommendIndexChange"
              @select-episode="onSelectEpisode"
            >
            </TheaterFeed>

            <DataFeedback
              v-else
              :loading="recommend.loading.value"
              :empty="!recommend.loading.value && recommend.formattedItems.value.length === 0"
              :finished="!recommend.hasMore.value"
              :empty-text="t('home.page.empty')"
              @refresh="() => recommend.loadMore(recommendPlayerRef)"
            />
          </section>

          <!-- 创作 -->
          <section class="relative h-full shrink-0" :style="{ width: gesture.sceneWidth.value }">
            <div v-if="activeTab === 'creation'" class="pointer-events-none absolute inset-x-0 top-16 z-30 px-2">
              <div
                ref="creationCategoryScrollRef"
                data-creation-category-scroll
                class="pointer-events-auto overflow-x-auto py-2"
                :style="{ scrollbarWidth: 'none', msOverflowStyle: 'none' }"
                @touchstart.stop="stopCategoryTouch"
                @touchmove.stop="stopCategoryTouch"
                @touchend.stop="stopCategoryTouch"
                @touchcancel.stop="stopCategoryTouch"
              >
                <div class="flex min-w-max gap-4 justify-center">
                  <button
                    v-for="category in creation.categories.value"
                    :key="category.Id"
                    :ref="setCreationCategoryButtonRef(category.Id)"
                    type="button"
                    class="shrink-0 font-medium text-sm lg:text-lg! transition-colors text-shadow-sm"
                    :class="
                      creation.activeCategoryId.value === category.Id ? 'text-white!' : 'text-white/70! hover:text-white'
                    "
                    @click="handleCreationCategoryChange(category.Id)"
                  >
                    {{ creation.getCategoryTitle(category) }}
                  </button>
                </div>
              </div>
            </div>

            <CreationFeed
              v-if="creation.items.value.length > 0"
              ref="creationPlayerRef"
              :items="creation.items.value"
              :playback-enabled="gesture.creationPlaybackEnabled.value"
              :navigation-enabled="gesture.creationNavigationEnabled.value"
              @index-change="onCreationIndexChange"
              @remix="onRemixCreation"
            >
            </CreationFeed>

            <DataFeedback
              v-else
              :loading="creation.loading.value"
              :empty="!creation.loading.value && creation.items.value.length === 0"
              :finished="!creation.hasMore.value"
              :empty-text="t('home.page.emptyCreation')"
              @refresh="() => creation.loadMore(creationPlayerRef)"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
[data-creation-category-scroll]::-webkit-scrollbar {
  display: none;
}
</style>
